// Web Push (browser/desktop/mobile) — subscribe/unsubscribe helpers.
// Standard Web Push via a service worker; alerts arrive even when the tab is backgrounded.
import api from "@/lib/api";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const pushPermission = () =>
  pushSupported() ? Notification.permission : "unsupported";

function urlB64ToUint8Array(base64) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getRegistration() {
  const existing = await navigator.serviceWorker.getRegistration("/push-sw.js");
  if (existing) return existing;
  return navigator.serviceWorker.register("/push-sw.js");
}

export async function getExistingSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.getRegistration("/push-sw.js");
    if (!reg) return null;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

// Enable push: request permission, subscribe, and store the subscription server-side.
export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, permission };

  const { data } = await api.get("/push/public-key");
  if (!data.enabled || !data.publicKey) throw new Error("push-not-configured");

  const reg = await getRegistration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.publicKey),
    });
  }
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return { ok: true, permission };
}

export async function disablePush() {
  const sub = await getExistingSubscription();
  if (sub) {
    try { await api.post("/push/unsubscribe", { endpoint: sub.endpoint }); } catch { /* */ }
    try { await sub.unsubscribe(); } catch { /* */ }
  }
  return { ok: true };
}

// Called on authenticated load: if the user already granted permission, make sure the
// current device's subscription is registered on the server (it can be dropped/rotated).
export async function ensurePushSubscribed() {
  if (!pushSupported() || Notification.permission !== "granted") return;
  try {
    const { data } = await api.get("/push/public-key");
    if (!data.enabled || !data.publicKey) return;
    const reg = await getRegistration();
    await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlB64ToUint8Array(data.publicKey),
      });
    }
    const json = sub.toJSON();
    await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  } catch { /* best-effort */ }
}
